<template>
    <v-container fluid class="pa-6">
      <!-- CABECERA -->
      <v-row class="mb-4" align="center">
        <v-col cols="12" md="6">
          <h1 class="headline font-weight-bold">Administración General de Tareas</h1>
          <p class="subtitle-1 grey--text">Supervisión de trabajos y asignación de personal</p>
        </v-col>
        <v-col cols="12" md="6" class="text-right">
          <v-btn color="primary" large depressed @click="advanceDialog = true">
            <v-icon left>mdi-plus-box</v-icon>
            Nuevo Trabajo Manual
          </v-btn>
        </v-col>
      </v-row>
  
      <!-- FILTROS Y BUSCADOR -->
      <v-card outlined class="rounded-xl mb-6">
        <v-card-title class="pa-4">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-text-field
                v-model="search"
                prepend-inner-icon="mdi-magnify"
                label="Buscar por máquina o plantilla..."
                outlined dense hide-details clearable
              ></v-text-field>
            </v-col>
            <v-col cols="12" md="3">
              <v-select
                v-model="filterStatus"
                :items="['Todos', 'abierto', 'incompleto']"
                label="Estado"
                outlined dense hide-details
              ></v-select>
            </v-col>
          </v-row>
        </v-card-title>
  
        <!-- TABLA DE TRABAJOS ACTIVOS -->
        <v-data-table
          :headers="headers"
          :items="trabajosActivos"
          :search="search"
          :loading="loading"
          class="bg-transparent"
        >
          <template v-slot:item.cliente="{ item }">
            <span class="text-body-2">
              {{ getCliente(item.id_empresa) }}
            </span>
          </template>
          <!-- 1. SLOT PARA LA FECHA (Formateada) -->
          <template v-slot:item.fecha_programada="{ item }">
            <span class="text-body-2">
              {{ formatDate(item.fecha_programada) }}
            </span>
          </template>

          <!-- 2. Slot Máquina -->
          <template v-slot:item.maquina="{ item }">
            <div class="font-weight-bold">{{ item.maquina }}</div>
            <div class="caption grey--text">{{ item.plantilla }}</div>
          </template>

          <!-- 3. Slot Estado -->
          <template v-slot:item.estado="{ item }">
            <v-chip :color="item.estado === 'abierto' ? 'info' : 'warning'" x-small dark>
              {{ item.estado.toUpperCase() }}
            </v-chip>
          </template>

          <!-- 4. SLOT TRABAJADOR ASIGNADO (Asegúrate que el header tenga value: 'operarios') -->
          <template v-slot:item.operarios="{ item }">
            <div class="d-flex flex-wrap" style="gap: 4px;">
              <!-- Comprobamos ids_operarios (que es el nombre que viene del backend) -->
              <template v-if="item.ids_operarios && item.ids_operarios.length > 0">
                <v-chip
                  v-for="idOp in item.ids_operarios"
                  :key="idOp"
                  x-small
                  outlined
                  color="primary"
                >
                  <v-icon left x-small>mdi-account</v-icon>
                  {{ getNombreOperario(idOp) }}
                </v-chip>
              </template>

              <v-chip v-else x-small outlined color="grey lighten-1" class="grey--text">
                Sin asignar
              </v-chip>
            </div>
          </template>

          <!-- 5. Acciones -->
          <template v-slot:item.actions="{ item }">
            <v-btn icon color="primary" small @click="editWorker(item)">
              <v-icon small>mdi-account-edit-outline</v-icon>
            </v-btn>
            <v-btn icon color="error" small @click="cancelJob(item)">
              <v-icon small>mdi-calendar-remove</v-icon>
            </v-btn>
          </template>
        </v-data-table>
      </v-card>
  
      <!-- MODAL REASIGNAR TRABAJADOR -->
      <v-dialog v-model="workerDialog" max-width="400px">
        <v-card class="rounded-xl pa-4">
          <v-card-title class="headline">Reasignar Operarios</v-card-title>
          <v-card-text>
            <p class="body-2">Selecciona los trabajadores para este preventivo:</p>
            <v-autocomplete
              v-model="selectedWorkersIds" 
              :items="operarios" 
              item-text="username"
              item-value="id"
              label="Reasignar operarios al trabajo"
              multiple
              chips
              outlined
              prepend-inner-icon="mdi-account-multiple"
            ></v-autocomplete>
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn text @click="workerDialog = false">Cancelar</v-btn>
            <v-btn color="primary" depressed :loading="isSaving" @click="saveNewWorker">Guardar Cambios</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-dialog v-model="advanceDialog" max-width="500px">
        <v-card class="rounded-xl">
          <v-toolbar color="secondary" dark flat dense>
            <v-toolbar-title>Adelantar Mantenimiento</v-toolbar-title>
          </v-toolbar>
          <v-card-text class="pa-6">
            <p class="body-2 mb-4">Selecciona una plantilla para generar un trabajo preventivo inmediato.</p>
            <v-autocomplete
              v-model="selectedPlantillaId"
              :items="plantillasActivas"
              item-text="nombre_completo"
              item-value="id"
              label="Buscar Plantilla / Máquina"
              outlined
              :loading="loadingPlantillas"
            >
              <template v-slot:item="{ item }">
                <v-list-item-content>
                  <v-list-item-title>{{ item.nombre_completo }}</v-list-item-title>
                  <v-list-item-subtitle>{{ item.maquina_nombre }}</v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-card-text>
          <v-card-actions class="pa-6">
            <v-spacer></v-spacer>
            <v-btn text @click="advanceDialog = false">Cancelar</v-btn>
            <v-btn 
              color="secondary" 
              depressed 
              :disabled="!selectedPlantillaId" 
              :loading="isAdvancing"
              @click="confirmarAdelantar"
            >
              Generar Trabajo
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
  
      <!-- Snackbar -->
      <v-snackbar v-model="notify.show" :color="notify.color" top>
        {{ notify.text }}
      </v-snackbar>
    </v-container>
  </template>
  
  <script>
  import axios from 'axios'
  import { mapActions } from 'vuex';
  
  export default {
    data() {
      return {
        loading: false,
        isSaving: false,
        isAdvancing: false,
        loadingPlantillas: false,
        advanceDialog: false,
        workerDialog: false,
        
        search: '',
        filterStatus: 'Todos',
        
        trabajosActivos: [],
        operarios: [],
        plantillasActivas: [],
        clientes: [],
        
        selectedJobId: null,
        selectedPlantillaId: null,
        selectedWorkersIds: [],
  
        headers: [
          { text: 'Máquina / Plan', value: 'maquina' },
          { text: 'Cliente', value: 'cliente' },
          { text: 'Fecha Programada', value: 'fecha_programada' },
          { text: 'Estado', value: 'estado', align: 'center' },
          { text: 'Operarios', value: 'operarios', sortable: false },
          { text: 'Acciones', value: 'actions', sortable: false, align: 'end' }
        ],
        notify: { show: false, text: '', color: 'success' }
      }
    },
    computed: {
      idEmpresa() {
        return localStorage.getItem("clienteSeleccionado");
      }
    },
    // Observamos el diálogo para cargar las plantillas solo cuando se abra
    watch: {
      advanceDialog(val) {
        if (val && this.plantillasActivas.length === 0) {
          this.loadPlantillas();
        }
      }
    },
    mounted() {
      this.initData();
    },
    methods: {
      ...mapActions(["getData", "postDataWithReturn"]),
      async initData() {
        if (!this.idEmpresa) {
          this.showNotify('No se ha seleccionado ninguna empresa', 'error');
          return;
        }
        await Promise.all([
          this.loadData(),
          this.loadUsuarios(),
          this.loadClientes(),
        ]);
      },
  
      // 1. Cargar trabajos abiertos e incompletos
      async loadData() {
        this.loading = true;
        try {
          const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/trabajos/admin-list`, {
            params: { id_empresa: this.idEmpresa }
          });
          this.trabajosActivos = res.data.trabajos || [];
        } catch (e) {
          this.showNotify('Error al cargar la lista de trabajos', 'error');
        } finally {
          this.loading = false;
        }
      },
  
      // 2. Cargar lista de trabajadores
      async loadUsuarios() {
        this.loading = true;
        try {
          const response = await this.getData({ path: "django/portal/get_all_users" });
          this.operarios = response.users.filter((c) => c.id_cliente == this.idEmpresa) || response;
        } catch (err) {
          this.showNotify('Error al cargar usuarios', 'error');
        } finally {
          this.loading = false;
        }
      },
  
      // 3. Cargar plantillas para el modal de "Adelantar"
      async loadPlantillas() {
        this.loadingPlantillas = true;
        try {
          const res = await axios.get(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/plantillas/activas`, {
            params: { id_empresa: this.idEmpresa }
          });
          this.plantillasActivas = (res.data.plantillas || []).map(p => ({
            id: p.id,
            nombre_completo: `${p.nombre} - ${p.maquina_nombre}`,
            maquina_nombre: p.maquina_nombre,
            tareas: p.tareas,
            operarios: p.ids_operarios,
          }));
        } catch (e) {
          this.showNotify('Error al cargar plantillas', 'error');
        } finally {
          this.loadingPlantillas = false;
        }
      },

      // 4. cargar clientes
      async loadClientes() {
        try {
          const response = await this.postDataWithReturn({ path: "django/portal/clientes/" });
          this.clientes = response.clientes || response;
          // let ramosCliente = this.clientes.find(c => c.id_cliente == 11956);
          // ramosCliente.id_cliente = 2000;
        } catch (err) {
          this.showNotify("Error al cargar clientes", "error");
        }
      },
  
      // 4. Abrir modal de reasignación
      editWorker(job) {
        this.selectedJobId = job.id;
        this.selectedWorkersIds = job.ids_operarios;
        this.workerDialog = true;
      },
  
      // 5. Guardar el nuevo trabajador asignado
      async saveNewWorker() {
        this.isSaving = true;
        try {
          await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/trabajos/${this.selectedJobId}/asignar`, {
            ids_operarios: this.selectedWorkersIds,
            id_empresa: this.idEmpresa
          });
          this.showNotify('Trabajador reasignado con éxito');
          this.workerDialog = false;
          await this.loadData();
        } catch (e) {
          this.showNotify('Error al reasignar', 'error');
        } finally {
          this.isSaving = false;
        }
      },
  
      // 6. Crear trabajo manual (Adelantar)
      async confirmarAdelantar() {
        if (!this.selectedPlantillaId) return;
        this.isAdvancing = true;
        try {
          const operarios_id = this.plantillasActivas.filter((p) => p.id == this.selectedPlantillaId)[0].operarios;

          await axios.post(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/trabajos/adelantar`, {
            plantilla_id: this.selectedPlantillaId,
            id_empresa: this.idEmpresa,
            operarios_id: operarios_id
          });
          
          this.showNotify('Trabajo generado correctamente');
          this.advanceDialog = false;
          this.selectedPlantillaId = null;
          this.selectedWorkersIds = [];
          await this.loadData(); // Refrescamos la lista
        } catch (e) {
          const msg = e.response?.data?.error || "Error al generar el trabajo";
          this.showNotify(msg, 'error');
        } finally {
          this.isAdvancing = false;
        }
      },
  
      // 7. Cancelar/Eliminar un trabajo pendiente
      async cancelJob(item) {
        if (!(await this.$confirmar({ titulo: "Cancelar trabajo", texto: `Se cancelará el trabajo de ${item.maquina}.`, aceptar: "Cancelar trabajo", cancelar: "Volver", peligro: true }))) return;
        
        try {
          await axios.delete(`${process.env.VUE_APP_LOCAL_BASE_URL}/api/nodejs/mantenimiento/trabajos/${item.id}`, {
            data: { id_empresa: this.idEmpresa }
          });
          this.showNotify('Trabajo cancelado correctamente');
          await this.loadData();
        } catch (e) {
          this.showNotify('No se pudo cancelar el trabajo', 'error');
        }
      },
  
      // Helper para notificaciones
      showNotify(text, color = 'success') {
        this.notify.text = text;
        this.notify.color = color;
        this.notify.show = true;
      },

      getCliente(id_empresa){
        return this.clientes.find((c) => c.id_cliente == id_empresa)?.nombre;
      },
  
      // Helper para formatear fechas en la tabla
      formatDate(date) {
        if (!date) return '-';
        return new Intl.DateTimeFormat('es-ES').format(new Date(date));
      },

      //Recoger nombre de usuario
      formatDate(dateStr) {
        if (!dateStr) return 'Sin fecha';
        const date = new Date(dateStr);
        // Usamos Intl para un formato localizado en español
        return new Intl.DateTimeFormat('es-ES', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
          // Si quieres hora añade: , hour: '2-digit', minute: '2-digit'
        }).format(date);
      },

      // OBTENER NOMBRE: Busca el ID en la lista que cargaste de la otra base de datos
      getNombreOperario(id) {
        // Supongamos que tu lista de operarios externos se llama 'listaOperariosExternos'
        const operario = this.operarios.find(u => u.id === id);
        if (operario) {
          return operario.username;
        }
        return `ID: ${id}`; // Fallback por si no encuentra el nombre
      },
    }
  }
  </script>